import React, { useEffect, useState } from 'react';
import { Controller, FieldPath, UseFormReturn } from 'react-hook-form';

import { AnimatePresence, motion } from 'framer-motion';
import { CheckIcon, PencilIcon, Receipt, TrashIcon, XIcon } from 'lucide-react';

import { ActionButton } from 'components/ActionButton';
import { Label } from 'components/Label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/Select';
import { RHFTextInput } from 'components/input/RHFTextInput';
import { Flex, Grid } from 'components/layout';
import { Typography } from 'components/typography';
import { Maybe } from 'types/UtilityTypes';

import {
  LTLAccessorial,
  QuickQuoteInputs,
  RATE_TYPE_OPTIONS_THREEG,
  ThreeGQuoteCharge,
} from '../../types';
import { CARD_ANIMATIONS as ANIMATIONS } from './cardAnimations';

const FIELD_PATH = 'ltl.quoteCharges';

const CHARGE_TYPE_OPTIONS = [
  { value: 'Accessorial', label: 'Accessorial' },
  { value: 'Freight', label: 'Freight' },
] as const;

interface QuoteChargeCardProps {
  index: number;
  formMethods: UseFormReturn<QuickQuoteInputs, any, QuickQuoteInputs>;
  onRemove: () => void;
  accessorials: LTLAccessorial[];
}

export function QuoteChargeCard({
  index,
  formMethods,
  onRemove,
  accessorials,
}: QuoteChargeCardProps) {
  const { control, watch, setValue } = formMethods;
  const [isEditing, setIsEditing] = useState(false);
  const [originalValues, setOriginalValues] =
    useState<Maybe<Partial<ThreeGQuoteCharge>>>(null);

  const watchedCharge = watch(`${FIELD_PATH}.${index}`);
  const isEmptyCharge =
    !watchedCharge?.chargeType &&
    !watchedCharge?.rateType &&
    !watchedCharge?.details;

  const handlers = {
    edit: () => {
      setOriginalValues({ ...watchedCharge });
      setIsEditing(true);
    },
    save: () => {
      setIsEditing(false);
      setOriginalValues(null);
    },
    discard: () => {
      if (originalValues) {
        const chargeId =
          watchedCharge?.chargeId ?? originalValues.chargeId ?? '';
        const fullSnapshot: ThreeGQuoteCharge = {
          chargeId,
          chargeType: originalValues.chargeType,
          rateType: originalValues.rateType,
          accCode: originalValues.accCode,
          details: originalValues.details,
          qty: originalValues.qty,
          netAmt: originalValues.netAmt,
          benchmark: originalValues.benchmark,
          netCost: originalValues.netCost,
          gainshare: originalValues.gainshare,
          grossAmt: originalValues.grossAmt,
          discAmt: originalValues.discAmt,
          thirdPartyChg: originalValues.thirdPartyChg,
        };
        setValue(
          `${FIELD_PATH}.${index}` as FieldPath<QuickQuoteInputs>,
          fullSnapshot,
          { shouldValidate: true }
        );
        setIsEditing(false);
        setOriginalValues(null);
      } else {
        onRemove();
      }
    },
    remove: onRemove,
  };

  useEffect(() => {
    if (isEmptyCharge && !isEditing) {
      setIsEditing(true);
    }
  }, [isEmptyCharge, isEditing]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && isEditing) {
      e.preventDefault();
      e.stopPropagation();
      handlers.save();
    }
  };

  const renderActions = () => (
    <AnimatePresence mode='wait'>
      {isEditing ? (
        <motion.div
          key='editing-actions'
          {...ANIMATIONS.actions}
          className='flex gap-1.5'
        >
          {!isEmptyCharge ? (
            <>
              <ActionButton
                onClick={handlers.save}
                className='hover:bg-info-200 text-info-600'
                icon={CheckIcon}
                aria-label='Save changes'
              />
              <ActionButton
                onClick={handlers.discard}
                className='hover:bg-error-200 text-error-600'
                icon={XIcon}
                aria-label='Discard changes'
              />
            </>
          ) : (
            <ActionButton
              onClick={handlers.remove}
              className='hover:bg-error-300 text-error-600'
              icon={TrashIcon}
              aria-label='Remove charge'
            />
          )}
        </motion.div>
      ) : (
        <motion.div
          key='view-actions'
          {...ANIMATIONS.actions}
          className='flex gap-1.5'
        >
          <ActionButton
            onClick={handlers.edit}
            className='hover:bg-info-300 text-info-600'
            icon={PencilIcon}
            aria-label='Edit charge'
          />
          <ActionButton
            onClick={handlers.remove}
            className='hover:bg-error-400 text-error-600'
            icon={TrashIcon}
            aria-label='Remove charge'
          />
        </motion.div>
      )}
    </AnimatePresence>
  );

  const summaryParts: string[] = [];

  if (watchedCharge?.chargeType) {
    summaryParts.push(watchedCharge.chargeType);
  }

  if (watchedCharge?.rateType) {
    summaryParts.push(watchedCharge.rateType);
  }

  if (watchedCharge?.details) {
    summaryParts.push(watchedCharge.details);
  }

  const summaryLabel =
    summaryParts.length > 0
      ? summaryParts.join(' • ')
      : `Quote Charge ${index + 1}`;

  const renderViewMode = () => (
    <motion.div
      key='view-mode'
      {...ANIMATIONS.toggle}
      className='overflow-hidden cursor-pointer'
      onClick={handlers.edit}
      onKeyDown={(e: React.KeyboardEvent) =>
        e.key === 'Enter' && handlers.edit()
      }
      role='button'
      tabIndex={0}
      aria-label='Click to edit quote charge'
    >
      <Flex justify='between' align='center' className='min-w-0'>
        <Flex align='center' gap='sm' className='flex-1 min-w-0 mr-2'>
          <Receipt className='h-4 w-4 text-neutral-500 shrink-0' />
          <Typography
            variant='body-sm'
            weight='medium'
            className='truncate'
            title={summaryLabel}
          >
            {summaryLabel}
          </Typography>
        </Flex>
        <div onClick={(e) => e.stopPropagation()} className='shrink-0'>
          {renderActions()}
        </div>
      </Flex>
    </motion.div>
  );

  const accCodeOptions = accessorials
    .filter((a) => a.externalId != null)
    .map((a) => ({
      value: String(a.externalId),
      label: `${a.code} - ${a.name}`,
    }));

  const renderEditingForm = () => (
    <motion.div
      key='editing-form'
      {...ANIMATIONS.toggle}
      className='overflow-hidden'
    >
      <Flex justify='between' align='center' className='mb-3'>
        <Typography variant='body-sm' weight='medium'>
          Edit Quote Charge
        </Typography>
        <div onClick={(e) => e.stopPropagation()}>{renderActions()}</div>
      </Flex>

      <Grid cols='2' gap='lg' className='w-full mx-0 mb-4'>
        <div className='w-full'>
          <Label name={`${FIELD_PATH}.${index}.chargeType`}>Charge Type</Label>
          <Controller
            name={`${FIELD_PATH}.${index}.chargeType`}
            control={control}
            render={({ field }) => (
              <Select onValueChange={field.onChange} value={field.value ?? ''}>
                <SelectTrigger className='w-full mt-1'>
                  <SelectValue placeholder='Select' />
                </SelectTrigger>
                <SelectContent>
                  {CHARGE_TYPE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </div>

        <div className='w-full'>
          <Label name={`${FIELD_PATH}.${index}.rateType`}>Rate Type</Label>
          <Controller
            name={`${FIELD_PATH}.${index}.rateType`}
            control={control}
            render={({ field }) => (
              <Select onValueChange={field.onChange} value={field.value ?? ''}>
                <SelectTrigger className='w-full mt-1'>
                  <SelectValue placeholder='Select' />
                </SelectTrigger>
                <SelectContent>
                  {RATE_TYPE_OPTIONS_THREEG.map((opt) => (
                    <SelectItem key={opt} value={opt}>
                      {opt}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </div>

        <div className='w-full'>
          <Label name={`${FIELD_PATH}.${index}.accCode`}>Acc Code</Label>
          <Controller
            name={`${FIELD_PATH}.${index}.accCode`}
            control={control}
            render={({ field }) => (
              <Select onValueChange={field.onChange} value={field.value ?? ''}>
                <SelectTrigger className='w-full mt-1'>
                  <SelectValue placeholder='Select' />
                </SelectTrigger>
                <SelectContent>
                  {accCodeOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </div>

        <RHFTextInput
          name={`${FIELD_PATH}.${index}.details`}
          label='Details'
          required={false}
        />
      </Grid>

      <Grid cols='3' gap='lg' className='w-full mx-0 mb-4'>
        <RHFTextInput
          name={`${FIELD_PATH}.${index}.qty`}
          label='Qty'
          inputType='number'
          placeholder='0'
          options={{ valueAsNumber: true, min: 0 }}
          required={false}
        />
        <RHFTextInput
          name={`${FIELD_PATH}.${index}.netAmt`}
          label='Net Amt'
          inputType='number'
          placeholder='0'
          options={{ valueAsNumber: true }}
          required={false}
        />
        <RHFTextInput
          name={`${FIELD_PATH}.${index}.benchmark`}
          label='Benchmark'
          inputType='number'
          placeholder='0'
          options={{ valueAsNumber: true }}
          required={false}
        />
        <RHFTextInput
          name={`${FIELD_PATH}.${index}.netCost`}
          label='Net Cost'
          inputType='number'
          placeholder='0'
          options={{ valueAsNumber: true }}
          required={false}
        />
        <RHFTextInput
          name={`${FIELD_PATH}.${index}.gainshare`}
          label='Gainshare'
          inputType='number'
          placeholder='0'
          options={{ valueAsNumber: true }}
          required={false}
        />
        <RHFTextInput
          name={`${FIELD_PATH}.${index}.grossAmt`}
          label='Gross Amt'
          inputType='number'
          placeholder='0'
          options={{ valueAsNumber: true }}
          required={false}
        />
      </Grid>

      <Grid cols='2' gap='lg' className='w-full mx-0'>
        <RHFTextInput
          name={`${FIELD_PATH}.${index}.discAmt`}
          label='Disc Amt'
          required={false}
          inputType='number'
          placeholder='0'
          options={{ valueAsNumber: true }}
        />
        <RHFTextInput
          name={`${FIELD_PATH}.${index}.thirdPartyChg`}
          label='3rd Party Chg'
          required={false}
          inputType='number'
          placeholder='0'
          options={{ valueAsNumber: true }}
        />
      </Grid>
    </motion.div>
  );

  return (
    <motion.div
      layout
      transition={{ type: 'spring', duration: 0.2, bounce: 0 }}
      {...ANIMATIONS.card}
      className='w-full overflow-hidden rounded-md border border-neutral-400 shadow-sm relative p-3 mb-2'
      onKeyDown={handleKeyDown}
    >
      <AnimatePresence mode='wait'>
        {isEditing ? renderEditingForm() : renderViewMode()}
      </AnimatePresence>
    </motion.div>
  );
}
